import axios from "axios";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import {
  collection,
  query,
  orderBy,
  getDocs,
  Timestamp,
  QueryConstraint,
} from "firebase/firestore";
import { auth, storage, db } from "./firebase";
import Logger from "../utils/logger";
import firebaseSettings from "../emulatorConfig";
import {
  Opinion,
  TopicListResponse,
  OpinionFilters,
  OpinionFormData,
  GenerateOpinionRequest,
  BulkAddRequest,
  BulkGenerateRequest,
  BulkGenerateResponse,
  WeeklyTopics,
  WeeklyTopicsWithDetails,
  WeekOption,
  AdminUser,
  AdminUserRole,
  CreateAdminUserRequest,
  AdminUserListResponse,
  PromptPreviewCategory,
  PromptPreviewClaimType,
  PromptPreviewResponse,
  RubricCatalog,
  RubricEvaluationVersion,
} from "../types";

// API base URL（emulatorConfig.tsで一元管理）
export const API_BASE_URL = firebaseSettings.apiUrl;

// API base URLを取得する関数
export const getApiBaseUrl = (): string => API_BASE_URL;

// Axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 1200000, // 20分に延長（GPT-5の生成に時間がかかるため）
});

// リクエストインターセプター（認証トークンを自動追加）
apiClient.interceptors.request.use(
  async (config) => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      const token = await currentUser.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// レスポンスインターセプター（エラーハンドリング）
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // 認証エラーの場合はログアウト処理
      auth.signOut();
    }
    return Promise.reject(error);
  }
);

// === 問題管理API ===

// 問題一覧取得
export const fetchOpinions = async (filters: OpinionFilters = {}): Promise<TopicListResponse> => {
  const params = new URLSearchParams();

  if (filters.page) params.append("page", filters.page.toString());
  if (filters.limit) params.append("limit", filters.limit.toString());
  if (filters.difficulty) params.append("difficulty", filters.difficulty.toString());
  if (filters.genre) params.append("genre", filters.genre);
  if (filters.isPublished !== undefined)
    params.append("isPublished", filters.isPublished.toString());
  if (filters.questionType) params.append("questionType", filters.questionType);
  if (filters.search) params.append("search", filters.search);

  Logger.debug("fetchOpinions - Request URL:", `/admin/topics?${params.toString()}`);
  Logger.debug("fetchOpinions - Filters:", filters);

  const response = await apiClient.get(`/admin/topics?${params.toString()}`);

  Logger.debug("fetchOpinions - Response:", response.data);
  Logger.debug("fetchOpinions - Total items:", response.data.pagination?.total);
  Logger.debug("fetchOpinions - Data length:", response.data.data?.length);

  return response.data;
};

// 問題作成
export const createOpinion = async (data: OpinionFormData): Promise<Opinion> => {
  const response = await apiClient.post("/admin/topics", data);
  return response.data;
};

// 問題更新
export const updateOpinion = async (
  id: string,
  data: Partial<OpinionFormData>
): Promise<Opinion> => {
  const response = await apiClient.put(`/admin/topics/${id}`, data);
  return response.data;
};

// 問題削除
export const deleteOpinion = async (id: string): Promise<void> => {
  await apiClient.delete(`/admin/topics/${id}`);
};

// AI問題生成（旧API - 後方互換性）
export const generateOpinion = async (data: GenerateOpinionRequest): Promise<Opinion> => {
  const response = await apiClient.post("/admin/generate-opinion", data);
  return response.data;
};

// 一括AI問題生成
export const bulkGenerateOpinions = async (
  data: BulkGenerateRequest
): Promise<BulkGenerateResponse[]> => {
  const response = await apiClient.post("/admin/bulk-generate-topics", data);
  return response.data;
};

// 一括追加
export const bulkAddOpinions = async (
  data: BulkAddRequest
): Promise<{ message: string; opinions: Opinion[] }> => {
  const response = await apiClient.post("/admin/bulk-add", data);
  return response.data;
};

// === アプリ用公開API（管理画面でのプレビュー用） ===

// 公開済み問題取得
export const fetchPublicOpinions = async (
  filters: { difficulty?: number; genre?: string; limit?: number } = {}
) => {
  const params = new URLSearchParams();
  if (filters.difficulty) params.append("difficulty", filters.difficulty.toString());
  if (filters.genre) params.append("genre", filters.genre);
  if (filters.limit) params.append("limit", filters.limit.toString());

  const response = await apiClient.get(`/opinions?${params.toString()}`);
  return response.data;
};

// 統計情報取得（問題登録状況）
export const fetchOpinionStats = async (): Promise<{
  stats: { [genre: string]: { [difficulty: number]: number } };
  meta?: { totalDocuments: number; validDocuments: number };
}> => {
  const response = await apiClient.get("/admin/stats");
  return response.data;
};

// ダッシュボード統計取得（回答数）
export const fetchDashboardStats = async (): Promise<import("../types").DashboardStats> => {
  const response = await apiClient.get("/admin/dashboard-stats");
  return response.data;
};

// 時系列統計取得（グラフ表示用）
export const fetchStatsTimeSeries = async (
  from: string,
  to: string
): Promise<import("../types").StatsTimeSeries> => {
  const response = await apiClient.get(`/admin/stats/timeseries?from=${from}&to=${to}`);
  return response.data;
};

// === 週間トピック設定API ===

// 週間トピック一覧取得
export const fetchWeeklyTopics = async (
  limit: number = 12
): Promise<{ weeklyTopics: WeeklyTopics[] }> => {
  const response = await apiClient.get(`/admin/weekly-topics?limit=${limit}`);
  return response.data;
};

// 週間トピック詳細取得
export const fetchWeeklyTopicsById = async (weekId: string): Promise<WeeklyTopicsWithDetails> => {
  const response = await apiClient.get(`/admin/weekly-topics/${weekId}`);
  return response.data;
};

// 週間トピック作成
export const createWeeklyTopics = async (data: {
  weekStartDate: string;
  topicIds: string[];
}): Promise<WeeklyTopics> => {
  Logger.debug("createWeeklyTopics - Request data:", data);
  try {
    const response = await apiClient.post("/admin/weekly-topics", data);
    Logger.debug("createWeeklyTopics - Response:", response.data);
    return response.data;
  } catch (error) {
    Logger.error("createWeeklyTopics - Error:", error);
    if (axios.isAxiosError(error)) {
      Logger.error("createWeeklyTopics - Response status:", error.response?.status);
      Logger.error("createWeeklyTopics - Response data:", error.response?.data);
    }
    throw error;
  }
};

// 週間トピック更新
export const updateWeeklyTopics = async (
  weekId: string,
  data: { topicIds: string[] }
): Promise<{ id: string; topicIds: string[]; updated: boolean }> => {
  Logger.debug("updateWeeklyTopics - weekId:", weekId);
  Logger.debug("updateWeeklyTopics - Request data:", data);
  try {
    const response = await apiClient.put(`/admin/weekly-topics/${weekId}`, data);
    Logger.debug("updateWeeklyTopics - Response:", response.data);
    return response.data;
  } catch (error) {
    Logger.error("updateWeeklyTopics - Error:", error);
    if (axios.isAxiosError(error)) {
      Logger.error("updateWeeklyTopics - Response status:", error.response?.status);
      Logger.error("updateWeeklyTopics - Response data:", error.response?.data);
    }
    throw error;
  }
};

// 週間トピック削除
export const deleteWeeklyTopics = async (
  weekId: string
): Promise<{ id: string; deleted: boolean }> => {
  const response = await apiClient.delete(`/admin/weekly-topics/${weekId}`);
  return response.data;
};

// 週リスト取得（設定対象週の選択用）
export const fetchWeekOptions = async (
  weeksAhead: number = 8,
  weeksBehind: number = 4
): Promise<{ weeks: WeekOption[] }> => {
  const response = await apiClient.get(
    `/admin/weekly-topics-weeks?weeksAhead=${weeksAhead}&weeksBehind=${weeksBehind}`
  );
  return response.data;
};

// 問題一覧取得（週間問題設定用）- Firestore直接アクセス版
export const fetchPublishedOpinionsForSelection = async (
  search?: string
): Promise<TopicListResponse> => {
  // 検索機能なしでFirestoreから直接取得
  const limitNum = search ? 50 : 10;

  const constraints: QueryConstraint[] = [orderBy("updatedAt", "desc")];

  const dataQuery = query(collection(db, "topics"), ...constraints);
  const snapshot = await getDocs(dataQuery);

  // 検索がある場合はクライアントサイドでフィルタリング
  let filteredDocs = snapshot.docs;
  if (search) {
    const searchLower = search.toLowerCase();
    filteredDocs = snapshot.docs.filter((docSnap) => {
      const data = docSnap.data();
      return (
        data.theme?.toLowerCase().includes(searchLower) ||
        data.content?.toLowerCase().includes(searchLower)
      );
    });
  }

  const paginatedDocs = filteredDocs.slice(0, limitNum);

  const opinions = paginatedDocs.map((docSnap) => {
    const data = docSnap.data();
    const createdAt = data.createdAt as Timestamp | null;
    const updatedAt = data.updatedAt as Timestamp | null;

    return {
      id: docSnap.id,
      ...data,
      createdAt: createdAt?.toDate?.().toISOString() || null,
      updatedAt: updatedAt?.toDate?.().toISOString() || null,
    } as Opinion;
  });

  return {
    data: opinions,
    pagination: {
      page: 1,
      limit: limitNum,
      total: filteredDocs.length,
      pages: 1,
    },
  };
};

// === 画像アップロードAPI ===

/**
 * 問題の画像をアップロード
 * @param opinionId 問題ID
 * @param file 画像ファイル
 * @returns アップロードされた画像のURL
 */
export const uploadOpinionImage = async (opinionId: string, file: File): Promise<string> => {
  // ファイル拡張子を取得
  const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
  const allowedExtensions = ["jpg", "jpeg", "png", "gif", "webp"];

  if (!allowedExtensions.includes(extension)) {
    throw new Error(
      "許可されていないファイル形式です。jpg, jpeg, png, gif, webpのみ対応しています。"
    );
  }

  // ファイルサイズチェック（5MB以下）
  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new Error("ファイルサイズは5MB以下にしてください。");
  }

  // Storage参照を作成
  const storageRef = ref(storage, `topics/${opinionId}/image.${extension}`);

  try {
    // アップロード実行
    Logger.debug(`Uploading image for opinion ${opinionId}...`);
    await uploadBytes(storageRef, file, {
      contentType: file.type,
    });

    // ダウンロードURLを取得
    const downloadUrl = await getDownloadURL(storageRef);
    Logger.debug(`Image uploaded successfully: ${downloadUrl}`);

    return downloadUrl;
  } catch (error) {
    Logger.error("Image upload failed:", error);
    throw new Error("画像のアップロードに失敗しました。");
  }
};

/**
 * 問題の画像を削除
 * @param opinionId 問題ID
 * @param imageUrl 現在の画像URL（拡張子を取得するため）
 */
export const deleteOpinionImage = async (opinionId: string, imageUrl: string): Promise<void> => {
  try {
    // URLから拡張子を抽出
    const urlPath = new URL(imageUrl).pathname;
    const extension = urlPath.split(".").pop()?.split("?")[0] || "jpg";

    const storageRef = ref(storage, `topics/${opinionId}/image.${extension}`);
    await deleteObject(storageRef);
    Logger.debug(`Image deleted for opinion ${opinionId}`);
  } catch (error) {
    Logger.error("Image deletion failed:", error);
    // 画像が存在しない場合はエラーを無視
  }
};

/**
 * 問題の画像URLを更新（Firestoreを更新）
 * @param opinionId 問題ID
 * @param imageUrl 画像URL（nullで削除）
 */
export const updateOpinionImageUrl = async (
  opinionId: string,
  imageUrl: string | null
): Promise<Opinion> => {
  const response = await apiClient.patch(`/admin/topics/${opinionId}/image`, {
    imageUrl,
  });
  return response.data;
};

// === 管理者ユーザー管理API ===

/**
 * 管理者ユーザー一覧取得
 */
export const fetchAdminUsers = async (): Promise<AdminUserListResponse> => {
  const response = await apiClient.get("/admin/users");
  return response.data;
};

/**
 * 管理者ユーザー作成
 */
export const createAdminUser = async (
  data: CreateAdminUserRequest
): Promise<{ success: boolean; user: AdminUser }> => {
  const response = await apiClient.post("/admin/users", data);
  return response.data;
};

/**
 * ユーザーロール更新
 */
export const updateUserRole = async (
  uid: string,
  role: AdminUserRole
): Promise<{ success: boolean; uid: string; newRole: AdminUserRole }> => {
  const response = await apiClient.patch(`/admin/users/${uid}/role`, { role });
  return response.data;
};

/**
 * 管理者ユーザー削除
 */
export const deleteAdminUser = async (uid: string): Promise<{ success: boolean; uid: string }> => {
  const response = await apiClient.delete(`/admin/users/${uid}`);
  return response.data;
};

// === プロンプト確認API ===

/**
 * プロンプト確認: 実際の評価で使われるプロンプトビルダー（v2.1.0）で組み立てた
 * 4軸分のプロンプトを取得する（OpenAI呼び出しなし）
 */
export const fetchPromptPreview = async (
  category: PromptPreviewCategory,
  claimType?: PromptPreviewClaimType
): Promise<PromptPreviewResponse> => {
  const response = await apiClient.get<PromptPreviewResponse>("/admin/prompt-preview", {
    params: { category, ...(claimType ? { claimType } : {}) },
  });
  return response.data;
};

// === 採点ルーブリックAPI ===

// バージョンごとの配信パス（全パターン明示列挙。新しい版を切ったらここに1行足す）
const RUBRIC_API_PATH_BY_VERSION: Record<RubricEvaluationVersion, string> = {
  "3.0.0": "/rubrics/v3.0.0",
};

/**
 * 採点ルーブリック取得（認証不要）
 * 実際に採点プロンプトへ埋め込まれている定義そのものを返す。
 * 表示したいバージョンは呼び出し側が指定する（画面側でバージョンを切り替えるため、
 * ここでバージョンを固定しない）。未デプロイの版を指定すると 404 になる。
 *
 * API はアプリ向けに Cache-Control: public, max-age=3600 を返すため、そのまま取得するとブラウザが
 * 1時間前の定義を表示し続ける（ルーブリックを書き換えても管理画面に出ない）。管理画面は編集中の定義を
 * 確かめる場所なので、取得時刻のクエリを付けて毎回ブラウザのキャッシュを通らないようにする
 * （サーバはクエリを読まない。アプリ側のキャッシュ方針は変えない）。
 */
export const fetchRubricCatalog = async (
  evaluationVersion: RubricEvaluationVersion
): Promise<RubricCatalog> => {
  const response = await apiClient.get<RubricCatalog>(
    RUBRIC_API_PATH_BY_VERSION[evaluationVersion],
    { params: { requestedAt: Date.now() } }
  );
  return response.data;
};

export default apiClient;
